import { TopbarArrow } from '@lt/components/icons-react/userflow';
import React from 'react';

import styled, { css } from 'styled-components';
import type { Step as StepType } from '../../types';
import { TEST_ID_TOPBAR_STEPPER_STEP } from './constants';

type Props = {
  currentStep: string;
  onClickStep: (newStep: string) => void;
  className?: string;
  steps: StepType[];
};

const TopbarStepper = ({
  currentStep,
  onClickStep,
  className,
  steps,
}: Props) => (
  <Steps className={className}>
    {steps.map((step, idx) => {
      const active = currentStep === step.id;
      const handleClick = () => {
        onClickStep(step.id);
      };

      return (
        <Step
          data-testid={TEST_ID_TOPBAR_STEPPER_STEP}
          key={step.id}
          $active={active}
          onClick={active ? undefined : handleClick}
        >
          <StepContent $first={idx === 0} $active={active}>
            <StepText>{step.title}</StepText>
          </StepContent>
          <Arrow
            $active={currentStep === step.id}
            $last={idx === steps.length - 1}
          />
        </Step>
      );
    })}
  </Steps>
);

const Steps = styled.div`
  display: flex;
  outline: 1px solid #c2c5cc;

  outline-offset: -1px;
  border-right: none;
  border-radius: 6px 0 0 6px;

  background-color: white;
  margin-right: 12px;
`;

const Step = styled.div<{ $active?: boolean }>`
  flex: 1;
  min-height: 38px;
  display: flex;
  position: relative;
  border-radius: 6px 0 0 6px;

  cursor: pointer;

  transition: 0.1s all ease-in-out;

  ${(props) =>
    props.$active &&
    css`
      z-index: 1;
      box-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1);
    `}
`;

const StepContent = styled.div<{ $active?: boolean; $first: boolean }>`
  padding: 7px 8px 7px 20px;

  display: flex;
  align-items: center;

  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #919499;

  min-height: 100%;
  flex: 1;

  transition: 0.25s all ease-in-out;

  ${(props) =>
    props.$active &&
    css`
      color: #17181a;
      border-radius: 6px 0 0 6px;
      outline: 1px solid #007aff;
      outline-offset: -1px;
    `}
`;

const StepText = styled.span`
  font-size: 10px;
  max-width: 90px;
`;

const Arrow = styled(TopbarArrow).attrs({ preserveAspectRatio: 'none' })<{
  $active: boolean;
  $last: boolean;
}>`
  width: 12px;
  height: 100%;
  position: absolute;
  right: -10px;
  fill: white;

  transition: 0.25s all ease-in-out;

  path {
    ${(props) =>
      props.$active
        ? css`
            z-index: 1;
          `
        : css`
            fill: none;
            stroke: none;
          `}

    ${(props) =>
      props.$last &&
      !props.$active &&
      css`
        stroke: #c2c5cc;
        fill: white;
      `}
  }
`;

export default TopbarStepper;
